import type { ConnectedHost, PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import type { serviceContainer } from '../../services/ServiceContainer';
import type { IMessageRouter } from '../../services/MessageRouter';
import { initializeMacroPackages } from '../macro/initializeMacroPackages';
import { initializeMacroGraphs } from './initializeMacroGraphs';
import { IncumbentNodeGraphConnection } from '../../services/macro-graph/IncumbentNodeGraphConnection';

interface SessionOwner { ownsSession(sessionId: string): boolean }
/** Session references attach after the existing peer stack initializes, without a second socket. */
export class InteractiveGraphHostTargets {
  private local: SessionOwner | null = null;
  private remote: SessionOwner | null = null;
  public constructor(private readonly machine: { readonly currentState: string }) {}
  public attach(local: SessionOwner, remote: SessionOwner): void { this.local = local; this.remote = remote; }
  public owns(host: ConnectedHost): boolean {
    return host.machineId === this.machine.currentState && (host.kind === 'local' ? this.local?.ownsSession(host.sessionId) === true : this.remote?.ownsSession(host.sessionId) === true);
  }
  public host(sessionId: string): ConnectedHost | null {
    const kind = this.local?.ownsSession(sessionId) ? 'local' : this.remote?.ownsSession(sessionId) ? 'remote' : null;
    return kind ? { kind, machineId: this.machine.currentState, sessionId } : null;
  }
}
type NativeMessages = (listener: (message: PlaycastMessage<PlaycastMessageTarget>) => void) => void;
type Services = Pick<typeof serviceContainer, 'ipcService' | 'realtimeService' | 'playjectorService'>;
/** Transport acknowledgement expiry reports uncertainty; it never cancels or retries graph execution. */
const ACKNOWLEDGEMENT_TIMEOUT_MS = 30000;

export function initializeHostGraphExecution(services: Services, router: IMessageRouter, receive: NativeMessages,
  receivePackages: NativeMessages, machine: { readonly currentState: string }) {
  const targets = new InteractiveGraphHostTargets(machine);
  const packages = initializeMacroPackages({ ipc: services.ipcService, realtime: services.realtimeService, realtimeRouter: router,
    receive, receiveMacroPackages: receivePackages, ownsVerificationHost: host => targets.owns(host) });
  const graphs = initializeMacroGraphs({ ipc: services.ipcService, realtime: services.realtimeService, router, receive,
    connection: new IncumbentNodeGraphConnection(),
    requestTimeoutMs: ACKNOWLEDGEMENT_TIMEOUT_MS, owns: host => targets.owns(host), host: sessionId => targets.host(sessionId) });
  return { packages, graphs, targets };
}
